import { useState } from "react";

interface FactoryUser {
  name: string;
  email: string;
  phone?: string;
  company: string;
  country: string;
  flag: string;
  address: string;
}

interface Props {
  factoryUser: FactoryUser;
  onClose: () => void;
}

interface ProductForm {
  name: string;
  description: string;
  price: string;
  minOrder: string;
  commission: string;
  category: string;
  stock: string;
  warehouseLocation: string;
  imageFile: string;
}

const emptyForm: ProductForm = {
  name: "", description: "", price: "", minOrder: "1",
  commission: "15", category: "", stock: "", warehouseLocation: "", imageFile: "",
};

const warehouses = [
  "Los Angeles, CA", "New York, NY", "Miami, FL", "Toronto, ON",
  "Vancouver, BC", "London, UK", "Frankfurt, Germany", "Rotterdam, Netherlands",
  "Paris, France", "Dubai, UAE", "Sydney, Australia", "Singapore", "Tokyo, Japan", "São Paulo, Brazil",
];

export default function FactoryProductPostModal({ factoryUser, onClose }: Props) {
  const [form, setForm] = useState<ProductForm>(emptyForm);
  const [previewImg, setPreviewImg] = useState<string>("");
  const [charCount, setCharCount] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = ev => setPreviewImg(ev.target?.result as string);
      reader.readAsDataURL(file);
      setForm(f => ({ ...f, imageFile: file.name }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!form.name || !form.price || !form.category || !form.stock || !form.warehouseLocation) {
      setError("Please fill in all required fields.");
      return;
    }
    setSubmitted(true);
    setTimeout(() => {
      onClose();
    }, 2500);
  };

  if (submitted) {
    return (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white rounded-2xl w-full max-w-md p-10 text-center" onClick={e => e.stopPropagation()}>
          <div className="w-20 h-20 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
            <i className="ri-checkbox-circle-fill text-green-500 text-4xl"></i>
          </div>
          <h3 className="text-xl font-extrabold text-gray-900 mb-2">Product Posted!</h3>
          <p className="text-gray-500 text-sm mb-1">Your product is now live for 600M+ buyers worldwide.</p>
          <p className="text-xs text-amber-500 font-semibold">{factoryUser.flag} {factoryUser.company}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="font-extrabold text-gray-900 text-base">Post New Product</h2>
            <p className="text-xs text-gray-400">{factoryUser.flag} {factoryUser.company} · {factoryUser.address}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 flex flex-col gap-4">
          {/* Image upload */}
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Product Photo *</label>
            <label className="flex flex-col items-center justify-center w-full h-44 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:border-amber-400 transition-colors bg-gray-50 overflow-hidden">
              {previewImg ? (
                <img src={previewImg} alt="preview" className="w-full h-full object-cover" />
              ) : (
                <div className="flex flex-col items-center gap-2 text-gray-400">
                  <i className="ri-image-add-line text-4xl"></i>
                  <span className="text-sm font-semibold">Click to upload product photo</span>
                  <span className="text-xs">JPG, PNG up to 10MB</span>
                </div>
              )}
              <input type="file" accept="image/*" className="hidden" onChange={handleImageChange} />
            </label>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Product Name *</label>
            <input
              required
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Wireless Earbuds Pro X200"
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">
              Description * <span className="text-gray-400 font-normal">({charCount}/500)</span>
            </label>
            <textarea
              required
              value={form.description}
              onChange={e => {
                if (e.target.value.length <= 500) {
                  setForm(f => ({ ...f, description: e.target.value }));
                  setCharCount(e.target.value.length);
                }
              }}
              placeholder="Describe your product features, specifications, and benefits..."
              rows={4}
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors resize-none"
            />
            {charCount >= 500 && <p className="text-red-500 text-xs mt-1">Maximum 500 characters reached.</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Unit Price (USD) *</label>
              <input
                required
                type="number"
                min="0.01"
                step="0.01"
                value={form.price}
                onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
                placeholder="29.99"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Min. Order (pcs) *</label>
              <input
                required
                type="number"
                min="1"
                value={form.minOrder}
                onChange={e => setForm(f => ({ ...f, minOrder: e.target.value }))}
                placeholder="1"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Commission Rate (%) *</label>
              <input
                required
                type="number"
                min="1"
                max="50"
                value={form.commission}
                onChange={e => setForm(f => ({ ...f, commission: e.target.value }))}
                placeholder="15"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Stock Quantity *</label>
              <input
                required
                type="number"
                min="1"
                value={form.stock}
                onChange={e => setForm(f => ({ ...f, stock: e.target.value }))}
                placeholder="1000"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Category *</label>
            <select
              required
              value={form.category}
              onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 cursor-pointer bg-white"
            >
              <option value="">Select category</option>
              {["Electronics", "Apparel", "Home & Garden", "Health & Beauty", "Sports & Fitness", "Auto Parts", "Food & Beverage", "Machinery"].map(c => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Overseas Warehouse Location *</label>
            <select
              required
              value={form.warehouseLocation}
              onChange={e => setForm(f => ({ ...f, warehouseLocation: e.target.value }))}
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 cursor-pointer bg-white"
            >
              <option value="">Select warehouse</option>
              {warehouses.map(w => <option key={w}>{w}</option>)}
            </select>
          </div>

          {/* Factory info preview */}
          <div className="bg-amber-50 rounded-xl p-3 flex items-center gap-3">
            <span className="text-2xl">{factoryUser.flag}</span>
            <div>
              <p className="text-xs font-bold text-amber-700">{factoryUser.company}</p>
              <p className="text-xs text-amber-600">{factoryUser.address}</p>
            </div>
          </div>

          {error && <p className="text-red-500 text-xs bg-red-50 px-3 py-2 rounded-lg">{error}</p>}

          <button
            type="submit"
            className="w-full bg-amber-400 hover:bg-amber-500 text-white font-bold py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-upload-cloud-line mr-2"></i>Post Product to Marketplace
          </button>
        </form>
      </div>
    </div>
  );
}
